import React, { useRef } from 'react';
import { Button } from './ui/Button';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Shield, Search, Bell, Activity, Lock, Gavel, FileText, Sparkles } from 'lucide-react';
import { slideUp, staggerContainer, fadeIn } from '../utils/animations';
import { trackCTAClick } from '../utils/analytics';
export function Hero() {
  const targetRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: targetRef,
    offset: ['start start', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.3]);
  const scale = useTransform(scrollYProgress, [0, 0.8], [1, 0.98]);
  const y = useTransform(scrollYProgress, [0, 0.8], [0, 30]);
  const symbolY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  return <section ref={targetRef} className="relative pt-24 pb-16 md:pt-32 md:pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-slate-50 min-h-[85vh] md:min-h-[90vh] flex flex-col justify-center" data-id="element-154">
      {/* Animated Background Gradients & Symbols */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-155">
        <motion.div animate={{
        scale: [1, 1.2, 1],
        rotate: [0, 10, 0],
        opacity: [0.4, 0.2, 0.4]
      }} transition={{
        duration: 15,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute top-[-20%] left-[-10%] w-[70%] h-[70%] bg-indigo-300/20 rounded-full mix-blend-multiply filter blur-[80px]" data-id="element-156" />
        <motion.div animate={{
        scale: [1, 1.1, 1],
        rotate: [0, -15, 0],
        opacity: [0.4, 0.2, 0.4]
      }} transition={{
        duration: 20,
        repeat: Infinity,
        ease: 'linear',
        delay: 2
      }} className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-amber-200/20 rounded-full mix-blend-multiply filter blur-[80px]" data-id="element-157" />

        {/* Floating Trademark Symbols with enhanced animation */}
        <motion.div style={{
        y: symbolY
      }} animate={{
        rotate: [0, 5, -5, 0]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-1/4 left-[10%] text-slate-200 text-6xl font-serif font-bold opacity-50" data-id="element-158">
          ®
        </motion.div>
        <motion.div style={{
        y: useTransform(scrollYProgress, [0, 1], [0, -50])
      }} animate={{
        rotate: [0, -5, 5, 0]
      }} transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-1/3 right-[15%] text-slate-200 text-4xl font-serif font-bold opacity-40" data-id="element-159">
          ™
        </motion.div>
        <motion.div style={{
        y: useTransform(scrollYProgress, [0, 1], [0, -150])
      }} animate={{
        rotate: [0, 3, -3, 0]
      }} transition={{
        duration: 12,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute bottom-1/4 left-[20%] text-slate-200 text-5xl font-serif font-bold opacity-30" data-id="element-160">
          ©
        </motion.div>

        {/* Floating sparkles */}
        {[...Array(5)].map((_, i) => <motion.div key={i} animate={{
        y: [0, -30, 0],
        opacity: [0, 1, 0],
        scale: [0, 1, 0]
      }} transition={{
        duration: 3,
        repeat: Infinity,
        delay: i * 0.6,
        ease: 'easeInOut'
      }} className="absolute" style={{
        left: `${20 + i * 15}%`,
        top: `${30 + i * 10}%`
      }} data-id="element-161">
            <Sparkles className="w-4 h-4 text-indigo-400" data-id="element-162" />
          </motion.div>)}

        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" data-id="element-163"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-164">
        <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-12 lg:gap-20" data-id="element-165">
          {/* Text Content */}
          <motion.div className="flex-1 text-center lg:text-left max-w-2xl mx-auto lg:mx-0" variants={staggerContainer} initial="hidden" animate="visible" data-id="element-166">
            <motion.div variants={fadeIn} className="flex justify-center lg:justify-start mb-5 md:mb-6" data-id="element-167">
              <motion.div whileHover={{
              scale: 1.05
            }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-slate-200 shadow-sm hover:border-indigo-200 transition-colors cursor-default" data-id="element-168">
                <span className="relative flex h-2.5 w-2.5" data-id="element-169">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" data-id="element-170"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" data-id="element-171"></span>
                </span>
                <span className="text-sm font-semibold text-slate-700" data-id="element-172">
                  Global IP Monitoring Active
                </span>
              </motion.div>
            </motion.div>

            <motion.h1 variants={slideUp} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 tracking-tight mb-5 md:mb-6 leading-[1.1]" data-id="element-173">
              The watchdog for your <br className="hidden lg:block" data-id="element-174" />
              <motion.span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-violet-600 to-amber-600" animate={{
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'linear'
            }} style={{
              backgroundSize: '200% 200%'
            }} data-id="element-175">
                trademark portfolio
              </motion.span>
            </motion.h1>

            <motion.p variants={slideUp} className="text-base sm:text-lg md:text-xl text-slate-600 mb-7 md:mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-id="element-176">
              Watchdog continuously scans global marketplaces, social media, and
              domain registries to detect trademark infringement and automate
              legal takedowns.
            </motion.p>

            <motion.div variants={slideUp} className="flex justify-center lg:justify-start" data-id="element-177">
              <a href="https://app.watchdog.buzz/" target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('hero')} data-id="element-178">
                <Button size="lg" variant="primary" className="w-full sm:w-auto min-h-[56px] text-lg px-10" icon={<ArrowRight className="w-5 h-5" data-id="element-180" />} data-id="element-179">
                  Start Free Trial
                </Button>
              </a>
            </motion.div>

            <motion.div variants={fadeIn} className="mt-6 md:mt-8 flex items-center justify-center lg:justify-start gap-4 md:gap-6 text-xs md:text-sm text-slate-500" data-id="element-181">
              <motion.div whileHover={{
              scale: 1.05
            }} className="flex items-center gap-2" data-id="element-182">
                <Shield className="w-4 h-4 text-green-500" data-id="element-183" />
                <span data-id="element-184">No credit card required</span>
              </motion.div>
              <motion.div whileHover={{
              scale: 1.05
            }} className="flex items-center gap-2" data-id="element-185">
                <Gavel className="w-4 h-4 text-indigo-500" data-id="element-186" />
                <span data-id="element-187">14-day free trial</span>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Visual Content - Dashboard Mockup */}
          <motion.div style={{
          opacity,
          scale,
          y
        }} className="flex-1 w-full max-w-xl lg:max-w-none perspective-1000" data-id="element-188">
            <motion.div initial={{
            rotateX: 10,
            rotateY: -10,
            opacity: 0,
            y: 50
          }} animate={{
            rotateX: 0,
            rotateY: 0,
            opacity: 1,
            y: 0
          }} transition={{
            duration: 1.2,
            type: 'spring',
            bounce: 0.1
          }} className="relative rounded-2xl bg-white shadow-2xl border border-slate-200/60 overflow-hidden ring-1 ring-slate-900/5" data-id="element-189">
              {/* Browser Chrome */}
              <div className="bg-slate-50/90 backdrop-blur-md border-b border-slate-200 px-4 py-3 flex items-center gap-4" data-id="element-190">
                <div className="flex space-x-1.5" data-id="element-191">
                  <motion.div whileHover={{
                  scale: 1.2
                }} className="w-3 h-3 rounded-full bg-red-400/80 cursor-pointer" data-id="element-192" />
                  <motion.div whileHover={{
                  scale: 1.2
                }} className="w-3 h-3 rounded-full bg-yellow-400/80 cursor-pointer" data-id="element-193" />
                  <motion.div whileHover={{
                  scale: 1.2
                }} className="w-3 h-3 rounded-full bg-green-400/80 cursor-pointer" data-id="element-194" />
                </div>
                <div className="flex-1 bg-white border border-slate-200 rounded-md px-3 py-1.5 flex items-center text-xs text-slate-400 shadow-sm" data-id="element-195">
                  <Lock className="w-3 h-3 mr-2 text-green-500" data-id="element-196" />
                  watchdog.ai/dashboard/trademarks
                </div>
              </div>

              {/* Dashboard UI */}
              <div className="p-6 bg-slate-50/50 min-h-[400px]" data-id="element-197">
                <div className="flex items-center justify-between mb-8" data-id="element-198">
                  <div data-id="element-199">
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-200">
                      Trademark Monitor
                    </h3>
                    <p className="text-xs text-slate-500" data-id="element-201">
                      Scanning for "Acme Corp" (Reg. #882910)
                    </p>
                  </div>
                  <div className="flex gap-2" data-id="element-202">
                    <motion.span animate={{
                    scale: [1, 1.05, 1]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity
                  }} className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-md flex items-center gap-1" data-id="element-203">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" data-id="element-204"></span>
                      Monitoring Active
                    </motion.span>
                  </div>
                </div>

                {/* Scanning Visual */}
                <div className="space-y-4" data-id="element-205">
                  {[{
                  name: 'USPTO Database',
                  status: 'Clean',
                  progress: 100,
                  color: 'bg-green-500',
                  icon: FileText
                }, {
                  name: 'Amazon Marketplace',
                  status: 'Counterfeits Detected',
                  progress: 100,
                  color: 'bg-red-500',
                  alert: true,
                  icon: Activity
                }, {
                  name: 'Domain Registries',
                  status: 'Scanning...',
                  progress: 65,
                  color: 'bg-amber-500',
                  icon: Search
                }, {
                  name: 'Social Media',
                  status: 'Scanning...',
                  progress: 32,
                  color: 'bg-blue-500',
                  icon: Bell
                }].map((item, i) => <motion.div key={item.name} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: 0.5 + i * 0.2
                }} whileHover={{
                  x: 5,
                  transition: {
                    duration: 0.2
                  }
                }} className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm relative overflow-hidden group hover:border-indigo-200 transition-colors cursor-default" data-id="element-206">
                      <div className="flex justify-between items-center mb-2 relative z-10" data-id="element-207">
                        <div className="flex items-center gap-2" data-id="element-208">
                          <item.icon className="w-4 h-4 text-slate-400" data-id="element-209" />
                          <span className="font-medium text-sm text-slate-700" data-id="element-210">
                            {item.name}
                          </span>
                        </div>
                        {item.alert ? <motion.span animate={{
                      scale: [1, 1.05, 1]
                    }} transition={{
                      duration: 1.5,
                      repeat: Infinity
                    }} className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full flex items-center gap-1" data-id="element-211">
                            <Activity className="w-3 h-3" data-id="element-212" />
                            Infringement Found
                          </motion.span> : <span className="text-xs text-slate-400" data-id="element-213">
                            {item.status}
                          </span>}
                      </div>
                      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden relative z-10" data-id="element-214">
                        <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: `${item.progress}%`
                    }} transition={{
                      duration: 2,
                      delay: 0.8 + i * 0.2,
                      ease: 'easeOut'
                    }} className={`h-full rounded-full ${item.color}`} data-id="element-215" />
                      </div>
                      {/* Hover Glow */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-indigo-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-216" />
                    </motion.div>)}
                </div>

                {/* Floating Alert Card */}
                <motion.div initial={{
                opacity: 0,
                y: 20,
                scale: 0.9
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} transition={{
                delay: 2.5,
                duration: 0.5
              }} whileHover={{
                y: -5,
                transition: {
                  duration: 0.2
                }
              }} className="absolute bottom-6 right-6 left-6 bg-slate-900 text-white p-4 rounded-xl shadow-xl border border-slate-700 flex items-start gap-3 z-20 cursor-default" data-id="element-217">
                  <motion.div animate={{
                  rotate: [0, 10, -10, 0]
                }} transition={{
                  duration: 2,
                  repeat: Infinity
                }} className="p-2 bg-red-500/20 rounded-lg" data-id="element-218">
                    <Activity className="w-5 h-5 text-red-400" data-id="element-219" />
                  </motion.div>
                  <div data-id="element-220">
                    <h4 className="font-bold text-sm" data-id="element-221">
                      Counterfeit Listing Detected
                    </h4>
                    <p className="text-xs text-slate-400 mt-1" data-id="element-222">
                      Unauthorized use of "Acme" trademark on Amazon.
                    </p>
                    <div className="mt-3 flex gap-2" data-id="element-223">
                      <motion.button whileHover={{
                      scale: 1.05
                    }} whileTap={{
                      scale: 0.95
                    }} className="text-xs bg-white text-slate-900 px-3 py-1.5 rounded-md font-bold hover:bg-slate-100 transition-colors" data-id="element-224">
                        Issue Takedown
                      </motion.button>
                      <motion.button whileHover={{
                      scale: 1.05
                    }} whileTap={{
                      scale: 0.95
                    }} className="text-xs text-slate-400 hover:text-white px-2 py-1.5 transition-colors" data-id="element-225">
                        View Evidence
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Decorative Elements behind dashboard */}
            <motion.div animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3]
          }} transition={{
            duration: 4,
            repeat: Infinity
          }} className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-indigo-500/5 rounded-full blur-3xl" data-id="element-226" />
          </motion.div>
        </div>
      </div>
    </section>;
}